import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import type { BuildingType } from "./shared/types";

// Model X/Z coordinates are fractions of the collision footprint. Nothing,
// including roof eaves, extends beyond its tiles. One cached draw call per model.
const cache = new Map<string, THREE.BufferGeometry>();
const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .94 });
const plaster = 0xd3bc8a, timber = 0x795332, dark = 0x493c2d;
const stone = 0x90968c, paleStone = 0xb6b6a1, roofGreen = 0x356d45, roofRed = 0xb16b45;
const slate = 0x425a70, ochre = 0xb58a45, iron = 0x39434b;

export function createDefensiveRange(radius: number): THREE.Mesh {
  const ring = new THREE.Mesh(new THREE.RingGeometry(radius - .045, radius, 160),
    new THREE.MeshBasicMaterial({ color: 0x241e13, side: THREE.DoubleSide, transparent: true,
      opacity: .85, depthWrite: false }));
  ring.name = "defensive-range";
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = .075;
  return ring;
}

export function createStoneFortification(type: "wall" | "gate", team: THREE.Color): THREE.Group {
  const root = new THREE.Group();
  const masonry = new THREE.MeshStandardMaterial({color:0x91958e, roughness:1});
  const trim = new THREE.MeshStandardMaterial({color:0xb6b6a1, roughness:1});
  const heraldry = new THREE.MeshStandardMaterial({color:team, roughness:.8});
  const iron = new THREE.MeshStandardMaterial({color:0x393b38, roughness:.8});
  const block = (parent: THREE.Group, w:number,h:number,d:number,x:number,y:number,z:number,material=masonry) => {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(w,h,d),material);
    mesh.position.set(x,y,z);mesh.castShadow=mesh.receiveShadow=true;parent.add(mesh);
  };
  if (type === "wall") {
    for (let row=0;row<5;row++) {
      for(let col=0;col<3;col++) block(root,.493,.39,.48,col*.5-.5,.2+row*.4,0,(row+col)%3===0?trim:masonry);
    }
    block(root,1.5,.12,.55,0,2.03,0,trim);
    for(const x of [-.55,0,.55]) block(root,.28,.35,.55,x,2.25,0,trim);
    block(root,.18,.52,.015,0,1.42,.25,heraldry);
    const connectors=new THREE.Group();connectors.name="wall-connectors";root.add(connectors);
  } else {
    for(const side of [-1,1]) {
      for(let row=0;row<6;row++) block(root,.55,.44,.72,side*1.5,.22+row*.45,0,row%2?masonry:trim);
      for(const x of [-.18,.18]) block(root,.2,.38,.72,side*1.5+x,2.9,0,trim);
      const leaf=new THREE.Group();leaf.name=side<0?"gate-left":"gate-right";leaf.position.x=side*1.35;
      for(let bar=0;bar<6;bar++) block(leaf,.085,2.2,.14,-side*(.12+bar*.235),1.1,0,iron);
      for(const y of [.3,1.1,1.95]) block(leaf,1.4,.12,.18,-side*.67,y,0,iron);
      root.add(leaf);
      block(root,.24,.65,.025,side*1.5,1.6,.375,heraldry);
    }
    block(root,3.6,.35,.75,0,2.65,0,trim);
    for(const x of [-.8,0,.8]) block(root,.33,.38,.75,x,3.01,0,trim);
  }
  return root;
}

export function createBuildingVisual(type: BuildingType, width: number, depth: number, team: THREE.Color): THREE.Mesh {
  const key = `${type}:${width}:${depth}:${team.getHexString()}`;
  let geometry = cache.get(key);
  if (!geometry) {
    const parts: THREE.BufferGeometry[] = [];
    const part = (shape: THREE.BufferGeometry, colour: number | THREE.Color,
      x: number, y: number, z: number, rx = 0, ry = 0) => {
      shape.rotateX(rx); shape.rotateY(ry); shape.translate(x, y, z);
      shape.deleteAttribute("uv");
      const g = shape.index ? shape.toNonIndexed() : shape;
      const color = new THREE.Color(colour);
      const colors = new Float32Array(g.getAttribute("position").count * 3);
      for (let i = 0; i < colors.length; i += 3) colors.set([color.r, color.g, color.b], i);
      g.setAttribute("color", new THREE.BufferAttribute(colors, 3));
      parts.push(g);
      if (g !== shape) shape.dispose();
    };
    const box = (w: number, h: number, d: number, c: number | THREE.Color, x = 0, y = h / 2, z = 0) =>
      part(new THREE.BoxGeometry(w, h, d), c, x, y, z);
    const roof = (w: number, h: number, d: number, x: number, y: number, z: number, c = roofGreen) => {
      const triangle = new THREE.Shape();
      triangle.moveTo(-w / 2, 0); triangle.lineTo(w / 2, 0); triangle.lineTo(0, h); triangle.closePath();
      const shape = new THREE.ExtrudeGeometry(triangle, { depth: d, bevelEnabled: false, steps: 1 });
      shape.translate(0, 0, -d / 2);
      part(shape, c, x, y, z);
      // Broad roof courses and ridge caps stay legible at gameplay zoom.
      const seam = new THREE.Color(c).multiplyScalar(.72);
      for (let row = 1; row < 4; row++) {
        const t = row / 4;
        for (const side of [-1, 1])
          box(.009, .045, d + .002, seam, x + side * w * (1 - t) / 2, y + h * t + .015, z);
      }
      box(.025, .08, d + .012, seam, x, y + h + .015, z);
    };
    const hall = (x: number, z: number, w: number, d: number, h: number, stoneWalls = false, c = roofGreen) => {
      box(w, h, d, stoneWalls ? stone : plaster, x, h / 2 + .12, z);
      box(w + .02, .14, d + .02, timber, x, h + .1, z);
      box(w + .018, .23, d + .018, stoneWalls ? paleStone : timber, x, .27, z);
      box(w + .016, .09, d + .016, timber, x, h * .55, z);
      for (const side of [-1, 1]) box(.024, h, d + .015, timber, x + side * w * .44, h / 2 + .12, z);
      roof(w + .055, .65, d + .055, x, h + .18, z, c);
      box(w * .2, h * .58, .018, dark, x, h * .29 + .12, z + d / 2 + .01);
      for (const side of [-1, 1]) {
        box(w * .13, .33, .022, dark, x + side * w * .31, h * .66 + .12, z + d / 2 + .012);
        box(w * .15, .06, .032, timber, x + side * w * .31, h * .66 - .08, z + d / 2 + .016);
        for (const offset of [-.25, .25]) {
          box(.015, .42, d * .18, dark, x + side * (w / 2 + .008), h * .64, z + offset * d);
          box(.022, .055, d * .21, paleStone, x + side * (w / 2 + .012), h * .64 - .23, z + offset * d);
        }
      }
      box(.026, .54, .02, timber, x, h + .42, z + d / 2 + .031);
    };
    const flag = (x: number, y: number, z: number) => {
      box(.014, .9, .014, dark, x, y + .45, z);
      box(.13, .35, .012, team, x + .057, y + .69, z);
    };
    const crate = (x: number, z: number, size = .11) => {
      box(size, .4, size, 0xa58046, x, .32, z);
      box(size + .005, .055, size + .005, timber, x, .22, z);
      box(size + .005, .055, size + .005, timber, x, .43, z);
    };
    const banner = (x: number, y: number, z: number, w = .12) => {
      box(w + .024, .07, .035, ochre, x, y + .4, z);
      box(w, .7, .023, team, x, y, z);
      box(w * .18, .4, .026, plaster, x, y + .04, z + .003);
    };
    const tower = (x: number, z: number, size: number, h: number) => {
      box(size, h, size, stone, x, h / 2 + .12, z);
      for (let level = .4; level < h; level += .65)
        box(size + .009, .045, size + .009, paleStone, x, level, z);
      for (const sx of [-1, 1]) for (const sz of [-1, 1])
        box(size * .16, h * .8, size * .16, paleStone, x + sx * size * .44, h * .4 + .12, z + sz * size * .44);
      box(size + .035, .2, size + .035, paleStone, x, h + .18, z);
      for (const sx of [-1, 0, 1]) for (const sz of [-1, 0, 1]) {
        if (sx === 0 && sz === 0) continue;
        box(size * .22, .38, size * .22, paleStone, x + sx * size * .37, h + .43, z + sz * size * .37);
      }
      box(.032, .4, .013, dark, x, h * .67, z + size / 2 + .01);
      box(.013, .4, .032, dark, x + size / 2 + .01, h * .67, z);
      banner(x, h * .42, z + size / 2 + .012, size * .3);
    };
    box(1, .12, 1, type === "camp" || type === "stable" ? 0x83764e : 0x9b9478);
    // Foundation edging communicates the full occupied square, including yards.
    for (const side of [-1, 1]) {
      box(1, .09, .025, paleStone, 0, .145, side * .4875);
      box(.025, .09, .95, paleStone, side * .4875, .145, 0);
    }
    switch (type) {
      case "house":
        hall(0, -.015, .8, .78, 1.75, false, roofRed);
        box(.12, .8, .14, stone, -.22, 2.25, -.2);
        box(.15, .12, .17, paleStone, -.22, 2.68, -.2);
        banner(.25, 1.1, .399, .1);
        crate(-.32, .41, .12);
        break;
      case "camp":
        // Open-sided timber depot, not another small house.
        box(.79, .17, .48, timber, 0, .22, -.16);
        for (const x of [-.35, .35]) for (const z of [-.35, .04])
          box(.038, 1.6, .038, timber, x, .95, z);
        box(.77, 1.2, .025, dark, 0, .84, -.36);
        roof(.85, .45, .5, 0, 1.78, -.16, ochre);
        for (let i = 0; i < 3; i++) {
          // Horizontal log pile beside the delivery yard.
          const log = new THREE.CylinderGeometry(.07, .07, .48, 8);
          log.rotateZ(Math.PI / 2);
          part(log, i % 2 ? timber : ochre, -.15, .3 + i * .16, .27);
        }
        crate(.25, .25, .2); crate(.22, -.18, .2); banner(.27, 1.15, .065);
        break;
      case "town_center":
        hall(0, -.23, .48, .43, 2.65, true, slate);
        hall(-.32, .12, .25, .59, 2.05, false, slate);
        hall(.32, .12, .25, .59, 2.05, false, slate);
        // Civic belfry: a tall, roofed landmark distinct from defensive towers.
        box(.2, 1.05, .2, plaster, 0, 3.56, -.22);
        box(.11, .65, .015, dark, 0, 3.65, -.112);
        part(new THREE.CylinderGeometry(.025, .055, .24, 8), ochre, 0, 3.7, -.095);
        roof(.27, .85, .27, 0, 4.13, -.22, slate);
        flag(.025, 5, -.22);
        for (const x of [-.3, .3]) banner(x, 1.35, .429);
        for (let i = 0; i < 3; i++) box(.23 + i * .05, .12, .065, paleStone, 0, .45 - i * .1, .13 + i * .065);
        box(.16, .9, .026, dark, 0, .83, -.007);
        break;
      case "barracks":
        hall(-.04, -.15, .74, .52, 1.8, true, roofRed);
        box(.64, .1, .04, timber, 0, 1.1, .3);
        for (const x of [-.23, 0, .23]) {
          box(.012, 1.3, .012, timber, x, .8, .3);
          part(new THREE.ConeGeometry(.035, .22, 4), paleStone, x, 1.52, .3);
          box(.1, .4, .035, team, x + .06, .8, .3);
        }
        flag(.3, 2.5, -.12);
        // Entrance guardhouse and large shield identify infantry production.
        tower(-.31, -.2, .19, 2.4);
        banner(.1, 1.45, .13, .2);
        break;
      case "archery_range":
        hall(0, -.24, .8, .35, 1.5, false, roofGreen);
        for (const x of [-.26, 0, .26]) {
          box(.025, .95, .025, timber, x, .55, .24);
          for (const [r, c, z] of [[.105, 0xdac78d, .25], [.07, 0x9c4438, .26], [.032, 0xe8d8b1, .27]]) {
            const target = new THREE.CylinderGeometry(r, r, .02, 12);
            // Correct circular target after footprint scaling in X/Z.
            target.rotateX(Math.PI / 2); target.scale(1, width, 1);
            part(target, c, x, 1, z);
          }
        }
        for (const x of [-.4, .4]) {
          box(.024, 1.05, .55, timber, x, .65, .16);
          box(.03, .1, .55, ochre, x, 1.2, .16);
        }
        flag(.28, 2.18, -.22);
        break;
      case "stable":
        hall(0, -.21, .8, .4, 2.05, false, ochre);
        for (const x of [-.23, .23]) box(.2, 1.35, .025, dark, x, .81, .005);
        for (const side of [-1, 1]) {
          box(.025, .8, .025, timber, side * .41, .52, .37);
          box(.025, .09, .57, timber, side * .41, .77, .1);
        }
        box(.25, .4, .13, 0x90643b, -.08, .84, .22);
        box(.07, .42, .08, 0x90643b, .04, 1.15, .22);
        box(.13, .19, .08, 0x65442c, .08, 1.37, .22);
        for (const x of [-.17, .01]) for (const z of [.17, .27]) box(.025, .52, .025, dark, x, .43, z);
        box(.17, .42, .18, 0xd5b667, -.29, .35, .26);
        box(.18, .035, .19, timber, -.29, .42, .26);
        // Oversized horseshoe sign reads even when the paddock horse is tiny.
        const shoe = new THREE.TorusGeometry(.075, .015, 4, 10, Math.PI * 1.55);
        shoe.scale(1, width, 1);
        part(shoe, iron, 0, 2.65, .027, 0, 0);
        flag(.3, 2.73, -.24);
        break;
      case "market":
        for (const x of [-.25, .25]) {
          box(.33, .65, .32, timber, x, .44, -.1);
          for (const side of [-1, 1]) box(.018, 1.6, .018, timber, x + side * .15, .9, .06);
          // One broad striped canopy per stall, rather than five tiny gables.
          for (let i = 0; i < 5; i++) {
            const cloth = new THREE.BoxGeometry(.069, .06, .44);
            part(cloth, i % 2 ? plaster : roofRed, x + (i - 2) * .071, 1.83, -.09, -.2);
            box(.069, .22, .018, i % 2 ? plaster : roofRed, x + (i - 2) * .071, 1.68, .128);
          }
          box(.34, .09, .1, plaster, x, .8, .09);
          crate(x, .3);
          for (let i = -1; i <= 1; i++)
            part(new THREE.IcosahedronGeometry(.045, 0), x < 0 ? 0xb25736 : 0x8b9c4a, x + i * .085, .95, .09);
        }
        flag(0, 1.7, -.32);
        break;
      case "tower":
        tower(0, 0, .74, 3.75);
        box(.8, .18, .8, iron, 0, 3.56, 0);
        for (const x of [-.33, .33]) box(.1, 1.15, .86, paleStone, x, .69, 0);
        flag(.22, 4.3, -.18);
        break;
      case "castle":
        for (const side of [-1, 1]) {
          box(.76, 1.9, .09, stone, 0, 1.07, side * .36);
          box(.09, 1.9, .76, stone, side * .36, 1.07, 0);
          for (let i = -2; i <= 2; i++) {
            box(.07, .32, .1, paleStone, i * .13, 2.17, side * .36);
            box(.1, .32, .07, paleStone, side * .36, 2.17, i * .13);
          }
          for (const z of [-.35, .35]) tower(side * .35, z, .23, 2.8);
        }
        hall(0, -.07, .38, .42, 4.05, true, slate);
        for (const x of [-.12, .12]) banner(x, 2.65, .155, .08);
        box(.17, 1.3, .02, dark, 0, .77, .412);
        for (const x of [-.06, -.02, .02, .06]) box(.012, 1.25, .024, timber, x, .74, .425);
        flag(.02, 4.88, -.06);
        break;
      default:
        throw new Error(`Building ${type} uses its existing specialised model`);
    }
    geometry = mergeGeometries(parts)!;
    // Preserve the original masonry/timber pieces for damage without extra draw calls.
    geometry.userData.parts = parts.map(p => p.getAttribute("position").count);
    for (const p of parts) p.dispose();
    geometry.scale(width, 1, depth);
    geometry.computeBoundingBox();
    cache.set(key, geometry);
  }
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = "building-model";
  mesh.userData.sharedGeometry = true;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}
